// Application Constants

export const STUDY_STATUS = {
  PENDING: "Pending",
  AUTHORITAS_IN_PROGRESS: "AuthoritasInProgress",
  AUTHORITAS_COMPLETED: "AuthoritasCompleted",
  GOOGLE_ADS_IN_PROGRESS: "GoogleAdsInProgress",
  COMPUTING: "Computing",
  COMPLETED: "Completed",
  FAILED: "Failed",
  CANCELLED: "Cancelled",
};

export const STATUS_CONFIG = {
  [STUDY_STATUS.PENDING]: {
    label: "En attente",
    color: "gray",
    badge: "En attente",
  },
  [STUDY_STATUS.AUTHORITAS_IN_PROGRESS]: {
    label: "Récupération SERP...",
    color: "blue",
    badge: "En cours",
  },
  [STUDY_STATUS.AUTHORITAS_COMPLETED]: {
    label: "SERP récupérées",
    color: "blue",
    badge: "En cours",
  },
  [STUDY_STATUS.GOOGLE_ADS_IN_PROGRESS]: {
    label: "Analyse volumes...",
    color: "blue",
    badge: "En cours",
  },
  [STUDY_STATUS.COMPUTING]: {
    label: "Calcul des KPIs...",
    color: "blue",
    badge: "En cours",
  },
  [STUDY_STATUS.COMPLETED]: {
    label: "Terminé",
    color: "green",
    badge: "Prêt",
  },
  [STUDY_STATUS.FAILED]: {
    label: "Échec",
    color: "red",
    badge: "Erreur",
  },
  [STUDY_STATUS.CANCELLED]: {
    label: "Analyse annulée",
    color: "gray",
    badge: "Annulée",
  },
};

export const POSITION_RANGES = {
  TOP_5: { min: 1, max: 5, label: "1-5", color: "var(--position-top5)" },
  TOP_10: { min: 6, max: 10, label: "6-10", color: "var(--position-top10)" },
  TOP_20: { min: 11, max: 20, label: "11-20", color: "var(--position-top20)" },
  BELOW_20: { min: 21, max: 100, label: "21+", color: "var(--position-below)" },
};

export const NAV_ITEMS = [
  { id: "dashboard", label: "Dashboard", icon: "Home", path: "/" },
  {
    id: "admin-studies",
    label: "Études bloquées",
    icon: "AlertTriangle",
    path: "/admin/studies",
    adminOnly: true,
  },
  {
    id: "admin-users",
    label: "Utilisateurs",
    icon: "Users",
    path: "/admin/users",
    adminOnly: true,
  },
];

export const POLLING_INTERVAL = 5000; // 5 seconds

// API Configuration
// In production (Azure SWA), use environment variable or fallback to Azure Functions URL
export const API_BASE_URL = process.env.REACT_APP_API_URL || "/api";

export const PAGINATION = {
  KEYWORDS_PER_PAGE: 100,
  PROJECTS_PER_PAGE: 6,
  LEADERS_PER_PAGE: 10,
};

export const SESSION_KEYS = {
  EXCLUDED_DOMAINS: "clustaar_excluded_domains",
  KEYWORD_SELECTED_DOMAINS: "clustaar_keyword_selected_domains",
};

export const METRIC_TOOLTIPS = {
  SHARE_OF_VOICE:
    "📊 Formule : SOV = (Trafic client / Trafic total tous les sites) × 100\n\n" +
    "💡 Mesure la part du trafic captée par votre site par rapport à l'ensemble des sites positionnés.\n\n" +
    "📝 Exemple : 1 mot-clé 'camping bretagne' (10k vol)\n" +
    "• Site A (pos 1, CTR 35%) → 3 500 visites\n" +
    "• Site B (pos 2, CTR 15%) → 1 500 visites\n" +
    "• Vous (pos 3, CTR 11%) → 1 100 visites\n\n" +
    "Trafic total = 3 500 + 1 500 + 1 100 = 6 100 visites\n" +
    "Votre SOV = (1 100 / 6 100) × 100 = 18.0%\n\n" +
    "✅ Vous captez 18% du trafic total sur ce mot-clé\n" +
    "📌 La somme des SOV de tous les sites = 100%",

  ESTIMATED_TRAFFIC:
    "📊 Formule : Trafic = Σ(Volume × CTR position)\n\n" +
    "💡 Estimation du nombre de visites SEO mensuelles.\n\n" +
    "📝 Exemple : 3 mots-clés positionnés\n" +
    "• 'camping bretagne' : 10 000 × 35% (pos 1) = 3 500\n" +
    "• 'location mobil-home' : 5 000 × 9% (pos 5) = 450\n" +
    "• 'vacances mer' : 8 000 × 2% (pos 10) = 160\n\n" +
    "Total = 4 110 visites/mois\n\n" +
    "📌 CTR par position : Pos 1 ≈ 35%, Pos 2 ≈ 25%, Pos 3 ≈ 18%, Pos 5 ≈ 9%, Pos 10 ≈ 2%",

  PRESENCE_RATE:
    "📊 Formule : (Mots-clés positionnés 1-100 / Total) × 100\n\n" +
    "💡 Pourcentage de mots-clés où vous êtes visible dans le Top 100.\n\n" +
    "📝 Exemple : Étude avec 100 mots-clés\n" +
    "• 85 mots-clés positionnés (positions 1-100)\n" +
    "• 15 mots-clés non positionnés\n\n" +
    "Taux = (85 / 100) × 100 = 85%\n\n" +
    "✅ Vous êtes visible pour 85% des mots-clés analysés",

  TOP_10:
    "📊 Formule : Count(mots-clés où position ∈ [1,10])\n\n" +
    "💡 Nombre de mots-clés positionnés en première page Google (positions 1-10).\n\n" +
    "📝 Calcul : Top5 (positions 1-5) + Positions 6-10\n\n" +
    "💡 Exemple : Étude avec 100 mots-clés\n" +
    "• 15 mots-clés en positions 1-5\n" +
    "• 27 mots-clés en positions 6-10\n" +
    "• → Top 10 = 42 mots-clés\n\n" +
    "⚠️ Gestion des doublons : Si votre domaine apparaît plusieurs fois pour le même mot-clé (ex: positions 2 et 15), seule la meilleure position est prise en compte.\n\n" +
    "✅ Métrique clé : Indique votre présence sur la première page Google, zone à fort trafic (>90% des clics).",

  AVERAGE_POSITION:
    "📊 Formule : Σ(positions) / Nombre de mots-clés positionnés\n\n" +
    "💡 Position moyenne sur vos mots-clés visibles (arrondi à 1 décimale).\n\n" +
    "📝 Exemple : 4 mots-clés positionnés\n" +
    "• Position 1\n" +
    "• Position 5\n" +
    "• Position 8\n" +
    "• Position 10\n\n" +
    "Moyenne = (1 + 5 + 8 + 10) / 4 = 6.0\n\n" +
    "✅ Interprétation :\n" +
    "• < 10 : Excellente visibilité\n" +
    "• 10-20 : Bonne visibilité\n" +
    "• > 20 : Visibilité faible, amélioration nécessaire",

  POSITIONED_DOMAINS:
    "📊 Formule : Count(domaines distincts avec au moins une position ∈ [1,100])\n\n" +
    "💡 Nombre de domaines différents qui apparaissent dans le Top 100 sur au moins un mot-clé de l'étude. Votre domaine est inclus.\n\n" +
    "📝 Exemple : Étude avec 3 mots-clés\n" +
    "• 'camping bretagne' → votre-site.fr, siteA.fr, siteB.fr\n" +
    "• 'location mobil-home' → votre-site.fr, siteB.fr\n" +
    "• 'vacances mer' → siteC.fr\n\n" +
    "Domaines positionnés = votre-site.fr, siteA.fr, siteB.fr, siteC.fr = 4\n\n" +
    "✅ Mesure la taille du paysage concurrentiel : plus le nombre est élevé, plus la visibilité est fragmentée entre de nombreux acteurs.\n\n" +
    "📌 Un domaine n'est compté qu'une fois, même s'il se positionne sur tous les mots-clés.",

  TOTAL_SEARCH_VOLUME:
    "📊 Formule : Σ(volume mensuel des mots-clés distincts)\n\n" +
    "💡 Volume de recherche mensuel cumulé du corpus de mots-clés de l'étude. Représente le marché total adressable en SEO.\n\n" +
    "📝 Exemple : Étude en SEO local\n" +
    "• 'plombier' (1 000 rech./mois) suivi à Paris ET à Lyon\n" +
    "• 'chauffagiste' (500 rech./mois) suivi à Paris\n\n" +
    "Volume total = 1 000 + 500 = 1 500 recherches/mois\n\n" +
    "⚠️ Chaque mot-clé ne compte qu'une seule fois : 'plombier' est suivi sur 2 localisations mais son volume n'est compté qu'une fois, car Google Ads renvoie un volume unique par terme de recherche.",

  KEYWORD:
    "Le terme de recherche exact analysé. Provient de votre liste de mots-clés importée. Utilisé pour récupérer les positions SERP et les volumes de recherche Google Ads.",

  VOLUME:
    "📊 Source : Google Ads API\n\n" +
    "💡 Volume de recherche mensuel moyen sur les 12 derniers mois.\n\n" +
    "📝 Exemple : 'camping bretagne' → 10 000 recherches/mois\n\n" +
    "⚠️ Représente uniquement les recherches exactes de ce terme (pas les variantes).",

  TREND:
    "📊 Graphique sparkline sur 12 mois\n\n" +
    "💡 Évolution du volume de recherche mensuel.\n\n" +
    "📝 Permet d'identifier :\n" +
    "• Tendances saisonnières (pics en été pour 'camping')\n" +
    "• Variations de popularité\n" +
    "• Mots-clés en croissance ou déclin\n\n" +
    "📌 Source : Google Ads",

  POSITION:
    "📊 Position dans les résultats Google\n\n" +
    "💡 Rang du domaine pour ce mot-clé (1-100).\n\n" +
    "📝 Exemple : Position 5 = 5ème résultat sur Google\n\n" +
    "⚠️ Gestion des doublons : Si le domaine apparaît plusieurs fois (ex: positions 5 et 15), seule la meilleure position (5) est affichée.\n\n" +
    "📌 Source : API Authoritas",

  KEYWORD_TRAFFIC:
    "📊 Formule : Volume × CTR de la position\n\n" +
    "💡 Estimation des clics mensuels pour ce mot-clé.\n\n" +
    "📝 Exemple : 'camping bretagne'\n" +
    "• Volume : 10 000 recherches/mois\n" +
    "• Position : 1 (CTR 35%)\n" +
    "• Trafic : 10 000 × 35% = 3 500 visites/mois\n\n" +
    "📌 CTR par position :\n" +
    "Pos 1: 35% | Pos 2: 25% | Pos 3: 18%\n" +
    "Pos 5: 9% | Pos 10: 2% | Pos 20: 0.8%",

  URL:
    "📊 URL exacte positionnée\n\n" +
    "💡 Page de votre site classée pour ce mot-clé.\n\n" +
    "📝 Exemple : https://example.com/camping-bretagne\n\n" +
    "✅ Permet d'identifier quelle page génère du trafic pour chaque mot-clé.\n\n" +
    "📌 Source : Données SERP Authoritas",

  DOMAIN:
    "📊 Domaine racine normalisé\n\n" +
    "💡 Nom de domaine extrait des URLs SERP.\n\n" +
    "📝 Normalisation :\n" +
    "• www.example.com → example.com\n" +
    "• blog.example.com → example.com\n" +
    "• shop.example.fr → example.fr\n\n" +
    "✅ Permet d'agréger toutes les positions d'un même concurrent.",

  POSITION_1_3:
    "🏆 Top 5 (Positions 1-5)\n\n" +
    "📊 Compte les mots-clés classés dans le top 5.\n\n" +
    "💡 Impact trafic : TRÈS ÉLEVÉ\n" +
    "• CTR cumulé : ~87% des clics de la page 1\n" +
    "• Position 1 : 35% CTR\n" +
    "• Position 2 : 25% CTR\n" +
    "• Position 3 : 18% CTR\n" +
    "• Position 4-5 : ~9% CTR\n\n" +
    "✅ Positions les plus stratégiques en SEO - Haut de page 1.\n\n" +
    "⚠️ Segments mutuellement exclusifs : un mot-clé n'est compté que dans UN seul segment.",

  POSITION_4_10:
    "📊 Top 10 (Positions 6-10)\n\n" +
    "💡 Impact trafic : MOYEN-ÉLEVÉ\n" +
    "• CTR : 6% à 2%\n" +
    "• Bas de première page Google\n" +
    "• Trafic modéré\n\n" +
    "📝 Exemple : Position 8 = 3% CTR\n" +
    "10 000 recherches × 3% = 300 visites/mois\n\n" +
    "⚠️ Segments mutuellement exclusifs : un mot-clé n'est compté que dans UN seul segment.",

  POSITION_11_20:
    "📊 Top 20 (Positions 11-20)\n\n" +
    "💡 Impact trafic : MOYEN\n" +
    "• CTR : 1.5% à 0.8%\n" +
    "• Deuxième page Google\n" +
    "• Trafic modéré\n\n" +
    "📝 Exemple : Position 15 = 1% CTR\n" +
    "10 000 recherches × 1% = 100 visites/mois\n\n" +
    "✅ Potentiel d'amélioration : passer en première page multiplie le trafic par 5-10×\n\n" +
    "⚠️ Segments mutuellement exclusifs : un mot-clé n'est compté que dans UN seul segment.",

  POSITION_21_PLUS:
    "📊 Below 20 (Positions 21-100)\n\n" +
    "💡 Impact trafic : FAIBLE\n" +
    "• CTR : < 0.5%\n" +
    "• Pages 3 et suivantes\n" +
    "• Trafic minimal\n\n" +
    "📝 Exemple : Position 30 = 0.3% CTR\n" +
    "10 000 recherches × 0.3% = 30 visites/mois\n\n" +
    "✅ Indique une présence du domaine, mais nécessite optimisation pour générer du trafic significatif.\n\n" +
    "⚠️ Segments mutuellement exclusifs : un mot-clé n'est compté que dans UN seul segment.",

  LEADERS_SHARE_OF_VOICE:
    "🏆 Top 10 des domaines classés par part de voix décroissante.\n\n" +
    "📊 Part de voix = trafic SEO estimé du domaine ÷ trafic total tous concurrents × 100.\n\n" +
    "🎨 Code couleur selon le rang dans le classement :\n" +
    "• Turquoise (Top 3) : les 3 plus gros leaders, dominent la thématique\n" +
    "• Bleu (Top 5) : 4ème et 5ème, challengers immédiats\n" +
    "• Gris (Top 10) : 6ème au 10ème, présents mais loin derrière\n\n" +
    "📏 La longueur de la barre est relative au leader n°1 (100% de sa valeur).",

  LEADERS_PRESENCE_RATE:
    "🎯 Top 10 des domaines classés par taux de présence décroissant.\n\n" +
    "📊 Taux de présence = part des mots-clés de l'étude où le domaine apparaît (positions 1-100).\n\n" +
    "🎨 Code couleur selon le rang dans le classement :\n" +
    "• Turquoise (Top 3) : les 3 domaines les plus présents sur la thématique\n" +
    "• Bleu (Top 5) : 4ème et 5ème, présence élevée\n" +
    "• Gris (Top 10) : 6ème au 10ème, présence plus limitée\n\n" +
    "📏 La longueur de la barre est relative au leader n°1 (100% de sa valeur).",

  PEOPLE_ALSO_ASK:
    "❓ Les questions du bloc « Autres questions posées » de Google, telles que remontées par notre fournisseur de données SERP.\n\n" +
    "⚠️ La détection est partielle : sur certains mots-clés le bloc existe dans Google mais n'est pas remonté. Un mot-clé absent de ce tableau n'a donc pas forcément de bloc « Autres questions ».\n\n" +
    "📌 Les questions sont regroupées par catégorie et par mot-clé. Pour un mot-clé suivi sur plusieurs adresses, les questions de toutes les adresses sont réunies.",
};

export const BREAKPOINTS = {
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  "2xl": 1536,
};
